import { closeDeal, createAlgoliaData, createDealApi, deleteAlgoliaData, fetchMatchedListingsBatch } from '../../util/api';
import { updatedEntities, denormalisedEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createImageVariantConfig } from '../../util/sdkLoader';
import { parse } from '../../util/urlHelpers';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 42 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE = 10;

// ================ Action types ================ //

export const FETCH_LISTINGS_REQUEST = 'app/ManageListingsPage/FETCH_LISTINGS_REQUEST';
export const FETCH_LISTINGS_SUCCESS = 'app/ManageListingsPage/FETCH_LISTINGS_SUCCESS';
export const FETCH_LISTINGS_ERROR = 'app/ManageListingsPage/FETCH_LISTINGS_ERROR';

export const OPEN_LISTING_REQUEST = 'app/ManageListingsPage/OPEN_LISTING_REQUEST';
export const OPEN_LISTING_SUCCESS = 'app/ManageListingsPage/OPEN_LISTING_SUCCESS';
export const OPEN_LISTING_ERROR = 'app/ManageListingsPage/OPEN_LISTING_ERROR';

export const CLOSE_LISTING_REQUEST = 'app/ManageListingsPage/CLOSE_LISTING_REQUEST';
export const CLOSE_LISTING_SUCCESS = 'app/ManageListingsPage/CLOSE_LISTING_SUCCESS';
export const CLOSE_LISTING_ERROR = 'app/ManageListingsPage/CLOSE_LISTING_ERROR';

export const ADD_OWN_ENTITIES = 'app/ManageListingsPage/ADD_OWN_ENTITIES';
export const UPSERT_MATCH_COUNTS = 'app/ManageListingsPage/UPSERT_MATCH_COUNTS';
export const FETCH_MATCHEDLISTINGS_REQUEST = 'app/MatchedListingsPage/FETCH_MATCHEDLISTINGS_REQUEST';


// ================ Reducer ================ //

const initialState = {
  pagination: null,
  queryParams: null,
  queryInProgress: false,
  queryListingsError: null,
  currentPageResultIds: [],
  ownEntities: {},
  matchedByKey:{},
  openingListing: null,
  openingListingError: null,
  closingListing: null,
  closingListingError: null,
  fetchMatchedListingsInProgress:false
};

const resultIds = data => data.data.map(l => l.id);

const merge = (state, sdkResponse) => {
  const apiResponse = sdkResponse.data;
  return {
    ...state,
    ownEntities: updatedEntities({ ...state.ownEntities }, apiResponse),
  };
};

const updateListingAttributes = (state, listingEntity) => {
  const oldListing = state.ownEntities.ownListing[listingEntity.id.uuid];
  const updatedListing = { ...oldListing, attributes: listingEntity.attributes };
  const ownListingEntities = {
    ...state.ownEntities.ownListing,
    [listingEntity.id.uuid]: updatedListing,
  };
  return {
    ...state,
    ownEntities: { ...state.ownEntities, ownListing: ownListingEntities },
  };
};

const manageListingsPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case FETCH_LISTINGS_REQUEST:
      return {
        ...state,
        queryParams: payload.queryParams,
        queryInProgress: true,
        queryListingsError: null,
        currentPageResultIds: [],
      };
    case FETCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        queryInProgress: false,
      };
    case FETCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryInProgress: false, queryListingsError: payload };

    case OPEN_LISTING_REQUEST:
      return {
        ...state,
        openingListing: payload.listingId,
        openingListingError: null,
      };
    case OPEN_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        openingListing: null,
      };
    case OPEN_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        openingListing: null,
        openingListingError: {
          listingId: state.openingListing,
          error: payload,
        },
      };
    }

    case CLOSE_LISTING_REQUEST:
      return {
        ...state,
        closingListing: payload.listingId,
        closingListingError: null,
      };
    case CLOSE_LISTING_SUCCESS:
      return {
        ...updateListingAttributes(state, payload.data),
        closingListing: null,
      };
    case CLOSE_LISTING_ERROR: {
      // eslint-disable-next-line no-console
      console.error(payload);
      return {
        ...state,
        closingListing: null,
        closingListingError: {
          listingId: state.closingListing,
          error: payload,
        },
      };
    }

    case ADD_OWN_ENTITIES:
      return merge(state, payload);

    case UPSERT_MATCH_COUNTS: {
      const { counts } = payload; 
      return {
        ...state,
        fetchMatchedListingsInProgress:false,
        matchedByKey: {...counts} ,
      };
    }
    case FETCH_MATCHEDLISTINGS_REQUEST: {
       return { ...state, fetchMatchedListingsInProgress: true,};
    }
    default:
      return state;
  }
};

export default manageListingsPageReducer;

// ================ Selectors ================ //

/**
 * Get the denormalised own listing entities with the given IDs
 *
 * @param {Object} state the full Redux store
 * @param {Array<UUID>} listingIds listing IDs to select from the store
 */
export const getOwnListingsById = (state, listingIds) => {
  const { ownEntities } = state.ManageListingsPage;
  const resources = listingIds.map(id => ({
    id,
    type: 'ownListing',
  }));
  const throwIfNotFound = false;
  return denormalisedEntities(ownEntities, resources, throwIfNotFound);
};

// ================ Action creators ================ //

// This works the same way as addMarketplaceEntities,
// but we don't want to mix own listings with searched listings
// (own listings data contains different info - e.g. exact location etc.)
export const addOwnEntities = sdkResponse => ({
  type: ADD_OWN_ENTITIES,
  payload: sdkResponse,
});

export const upsertMatchCounts = counts => ({
  type: UPSERT_MATCH_COUNTS,
  payload: { counts },
});

export const fetchMatchListings = counts => ({
  type: FETCH_MATCHEDLISTINGS_REQUEST,
});


export const openListingRequest = listingId => ({
  type: OPEN_LISTING_REQUEST,
  payload: { listingId },
});


export const openListingSuccess = response => ({
  type: OPEN_LISTING_SUCCESS,
  payload: response.data,
});

export const openListingError = e => ({
  type: OPEN_LISTING_ERROR,
  error: true,
  payload: e,
});

export const closeListingRequest = listingId => ({
  type: CLOSE_LISTING_REQUEST,
  payload: { listingId },
});

export const closeListingSuccess = response => ({
  type: CLOSE_LISTING_SUCCESS,
  payload: response.data,
});

export const closeListingError = e => ({
  type: CLOSE_LISTING_ERROR,
  error: true,
  payload: e,
});

export const queryListingsRequest = queryParams => ({
  type: FETCH_LISTINGS_REQUEST,
  payload: { queryParams },
});

export const queryListingsSuccess = response => ({
  type: FETCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const queryListingsError = e => ({
  type: FETCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

// Throwing error for new (loadData may need that info)
export const queryOwnListings = queryParams => (dispatch, getState, sdk) => {
  dispatch(queryListingsRequest(queryParams));

  const { perPage, ...rest } = queryParams;
  const params = { ...rest, perPage };

  return sdk.ownListings
    .query(params)
    .then(response => {
      dispatch(addOwnEntities(response));
      dispatch(queryListingsSuccess(response));
      dispatch(fetchMatchedListings({ listings: response?.data?.data || [] }));
      return response;
    })
    .catch(e => {
      dispatch(queryListingsError(storableError(e)));
      throw e;
    });
};

export const closeListing = (listingId, pipedriveDealId, isCompany = true) => (dispatch, getState, sdk) => {
  dispatch(closeListingRequest(listingId));

  return sdk.ownListings
    .close({ id: listingId }, { expand: true })
    .then(response => {
      deleteAlgoliaData({ id: (listingId && listingId.uuid) ? listingId.uuid : listingId, indexName: !isCompany ? process.env.REACT_APP_ALGOLIA_PRO_LISTING_INDEX : process.env.REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX }).then(() => {
        if (isCompany) {
          dispatch(closeListingSuccess(response));
        }

      })
      return response;
    })
    .catch(e => {
      dispatch(closeListingError(storableError(e)));
    });
};

export const openListing = (listingId, pipedriveDealId, isCompany = true) => (dispatch, getState, sdk) => {
  dispatch(openListingRequest(listingId));

  return sdk.ownListings
    .open({ id: listingId }, { expand: true })
    .then(response => {
      if (isCompany) {
        dispatch(openListingSuccess(response));
      }
      const { currentUser } = getState().user;
      const { email } = currentUser.attributes;

      const {
        firstName,
        lastName,
        displayName,
        publicData: userPublicData
      } = currentUser.attributes.profile;

      const { pipedriveUserId, accountType } = userPublicData || {};

      const {
        title = "",
        description = "",
        price,
        state,
        geolocation,
        availabilityPlan = '',
        publicData = {}
      } = response.data.data.attributes || {};
      const { people, portfolio, pipedriveDealId, addSelfie1,skillsInOne,
        ...restPublicData } = publicData || {};
      const { uuid: customerId } = (currentUser && currentUser.id) || {};
      const { experiences, roles } = restPublicData || {};
      const skills = { label: [] };
      if (skillsInOne && skillsInOne.length) {
        skillsInOne.forEach(({ childSkills = [] }) => {
          if (childSkills && childSkills.length) {
            childSkills?.forEach(({ label }) => {
              skills?.label.push(label);
            });
          }
        });
      }
      const algoliaObj = {
        indexName: userPublicData.accountType == 'fractionalPro'
          ? process.env.REACT_APP_ALGOLIA_PRO_LISTING_INDEX
          : process.env.REACT_APP_ALGOLIA_COMPANY_LISTING_INDEX,
        objectID: (listingId && listingId.uuid) ? listingId.uuid : listingId,
        _geoloc: geolocation && geolocation.lat ? geolocation : {},
        title, description, price, availabilityPlan,skills,
        ...publicData,
        // Only include safe user fields for Algolia - DO NOT include email, firstName, lastName, phoneNumber
        user: { displayName, ...userPublicData }
      };
      const modifiedExperience = [];
      let dealMoney = 0;
      (experiences && experiences.length && Array.isArray(experiences)) ? experiences.map((data, i) => {
        if (data && data.label && data.inputValues && data.inputValues.price) {
          if (i == 0) {
            dealMoney = (data.inputValues.price) / 100;
          }
          modifiedExperience.push({ experienceRole: data.label, earnedMoney: (data.inputValues.price) / 100 })
        }
      }) : null;

      (roles && roles.length && Array.isArray(roles) ? roles.map((data, i) => {
        if (data && data.label && data.perHourRate) {
          if (i == 0) {
            dealMoney = (data.perHourRate.value) / 100;
          }
          modifiedExperience.push({
            experience
              : (data && data.experience) ? data.experience : []
            , perHourRate: (data.perHourRate) / 100, availableHoursPerWeek: (data && data.availableHoursPerWeek) ? data.availableHoursPerWeek : null
          })
        }
      }) : null)
      try {
        createAlgoliaData(algoliaObj);
        if (pipedriveUserId) {
          const dealData = {
            customerId,
            email,
            listingId: listingId?.uuid,
            accountType,
            title,
            modifiedExperience,
            dealMoney,
            pipedriveUserId,
            addSelfie1
          }
          // createDealApi(dealData).then((res) => {
          //   const { id: pipedriveDealId } = (res && res.data && res.data.data) || {};
          //   if (pipedriveDealId) {
          //     const updateData = {
          //       id: listingId?.uuid,
          //       publicData: {
          //         pipedriveDealId
          //       }
          //     }
          //     sdk.ownListings.update(updateData);
          //   }
          // })

        }
      } catch (error) {
        console.log(error, '**** **** => error');
      }
      return response;
    })
    .catch(e => {
      dispatch(openListingError(storableError(e)));
    });
};

export const fetchMatchedListings = params => async (dispatch, getState) => {
  dispatch(fetchMatchListings())
  try {
    const listings = params?.listings || {};
    const toItem = l => {
      const role = l?.attributes?.publicData?.roles?.[0];
      const title = role?.label;
      const specialties = role?.specialties || role?.specialtiesArray || [];
      const tools = role?.favouriteToolArray || role?.favouriteTool || [];

      return {
        key: `${String(title).toLowerCase()}|${JSON.stringify(specialties)}|${JSON.stringify(tools)}`,
        listingId: l?.id?.uuid,
        title,
        specialties,
        tools,
      };
    };

    const map = new Map();
    if (Array.isArray(listings)) {
      listings.forEach(l => {
        const item = toItem(l);
        if (item && !map.has(item.key)) {
          map.set(item.key, item);
        }
      });
    } else {
      const item = toItem(listings);
      if (item && !map.has(item.key)) {
        map.set(item.key, item);
      }
    }

    const items = Array.from(map.values());
    // if (items.length === 0) return {};

    const res = await fetchMatchedListingsBatch({ items });
    const counts = (res && res.data) || {};     
    dispatch(upsertMatchCounts(counts));
    return counts;
  } catch (e) {
    console.error('batch match fetch failed', e);
    return {};
  }
};

export const loadData = (params, search, config) => {
  const queryParams = parse(search);
  const page = queryParams.page || 1;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  return queryOwnListings({
    ...queryParams,
    page,
    perPage: RESULT_PAGE_SIZE,
    include: ['images', 'currentStock'],
    'fields.image': [`variants.${variantPrefix}`, `variants.${variantPrefix}-2x`],
    ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
    ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
    'limit.images': 1,
  });
};
